import Icon from '@/components/base/icon/phosphor-icon';
import { Link } from '@/navigation';
import { containsClasses } from '@/utils/contains-classes';
import { CircleNotch } from '@phosphor-icons/react/dist/ssr';
import { clsx } from 'clsx';
import type { ComponentPropsWithoutRef } from 'react';

interface BaseProps extends ComponentPropsWithoutRef<'button'> {
  variant: 'primary' | 'secondary' | 'tertiary' | 'tertiary-no-frame';
  size?: 'default' | 'small' | 'large';
  disabled?: boolean;
  accented?: boolean;
  inverted?: boolean;
  isLoading?: boolean;
  testId?: string;
  linkRestProps?: ComponentPropsWithoutRef<'a'>;
}

export type ButtonLinkProps =
  | {
      isLink: true;
      href: string;
      prefetch?: boolean;
    }
  | {
      isLink?: false;
      href?: never;
      prefetch?: boolean;
    };

type ButtonProps = BaseProps & ButtonLinkProps;

const positionClasses = ['relative', 'absolute', 'fixed'];

const baseClasses = clsx(
  'anim-effect focus-visible:focus-outline rounded-s font-semibold focus-visible:z-20 s:max-w-max-button-width',
);

const baseInvertedClasses = clsx('focus-visible:!outline-white');

const disabledClasses = clsx('bg-alt text-muted');

const variantClasses = {
  primary: clsx('text-inverted hover:outline'),
  secondary: clsx(
    'bg-main outline-hover-brand ring ring-inset ring-strong hover:outline hover:outline-3 hover:ring-brand',
  ),
  tertiary: clsx(
    'bg-main outline-hover-brand ring-1 ring-inset ring-decorative hover:outline hover:outline-3 hover:ring-brand',
  ),
  'tertiary-no-frame': clsx(
    'bg-transparent outline-hover hover:bg-main hover:outline hover:outline-3 focus-visible:bg-main',
  ),
};

const nonAccentedClasses = {
  primary: clsx('bg-contrast hover:bg-brand hover:outline-hover-brand'),
  secondary: clsx('border-strong text-strong'),
  tertiary: clsx('text-strong'),
  'tertiary-no-frame': clsx('text-strong'),
};

const accentedClasses = {
  primary: clsx('bg-brand hover:bg-contrast hover:outline-hover'),
  secondary: clsx('border-brand text-brand outline-hover hover:border-strong'),
  tertiary: clsx('text-brand'),
  'tertiary-no-frame': clsx('text-brand'),
};

const invertedClasses = {
  primary: variantClasses.primary,
  secondary: variantClasses.secondary,
  tertiary: clsx(
    'bg-transparent text-inverted ring-1 ring-inset ring-decorative hover:ring-2',
  ),
  'tertiary-no-frame': clsx(variantClasses['tertiary-no-frame']),
};

const heightClasses = {
  default: clsx('h-clickable'),
  small: clsx('h-clickable-minimum'),
  large: clsx('h-clickable-large'),
};

const paddingClasses = {
  primary: {
    default: clsx('px-m'),
    small: clsx('px-s'),
    large: clsx('px-l'),
  },
  secondary: {
    default: clsx('px-m'),
    small: clsx('px-s'),
    large: clsx('px-l'),
  },
  tertiary: {
    default: clsx('px-s'),
    small: clsx('px-xs'),
    large: clsx('px-m'),
  },
  'tertiary-no-frame': {
    default: clsx('px-xxs'),
    small: clsx('p-xxs'),
    large: clsx('px-xxs'),
  },
};

const loadingIconClasses = {
  default: 'icon-m',
  small: 'icon-s',
  large: 'icon-l',
};

const Button = ({
  variant,
  size = 'default',
  accented,
  disabled,
  inverted,
  isLoading,
  children,
  className,
  isLink = false,
  prefetch = false,
  href,
  testId,
  linkRestProps,
  ...props
}: ButtonProps) => {
  const classNames = clsx(
    baseClasses,
    heightClasses[size],
    paddingClasses[variant][size],
    inverted && baseInvertedClasses,
    !disabled && !inverted && variantClasses[variant],
    !accented && !disabled && !inverted && nonAccentedClasses[variant],
    accented && !disabled && !inverted && accentedClasses[variant],
    disabled && disabledClasses,
    !disabled && inverted && invertedClasses[variant],
    isLoading && 'pointer-events-none',
    !containsClasses(className, positionClasses) && 'relative',
    className,
  );

  return !isLink ? (
    <button
      className={classNames}
      disabled={disabled}
      aria-disabled={isLoading} // prevent blurring when button is in loading state
      data-testid={testId}
      {...props}
    >
      <div
        className={clsx(
          'flex items-center justify-center gap-xxs no-flex-gap:space-x-xxs',
          isLoading && 'invisible',
        )}
      >
        {children}
      </div>
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon
            Icon={CircleNotch}
            variant="bold"
            className={clsx('animate-spin', loadingIconClasses[size])}
          />
        </div>
      )}
    </button>
  ) : (
    <Link
      href={href ?? '/'}
      aria-label={props['aria-label']}
      prefetch={prefetch}
      className={clsx(classNames, 'block', disabled && 'pointer-events-none')}
      data-testid={testId}
      {...linkRestProps}
    >
      <div className="flex h-full items-center justify-center gap-xxs no-flex-gap:space-x-xxs">
        {children}
      </div>
    </Link>
  );
};

export default Button;
